<template>
  <div>
    <AdminHeader />

    <!-- Page Header -->
    <div class="page-header">
      <div>
        <h5 class="page-title">Business Bank Accounts</h5>
        <p class="page-sub">Manage bank accounts used across your business</p>
      </div>

      <div class="page-actions">
        <div class="search-wrap">
          <font-awesome-icon icon="search" class="search-icon" />
          <input
            type="text"
            class="search-input"
            placeholder="Search accounts..."
            v-model="search"
          />
        </div>

        <button class="btn-primary-action" @click="openCreate" :disabled="permission.create_bank != 1">
          <font-awesome-icon icon="plus" /> Add Bank Account
        </button>
      </div>
    </div>

    <!-- Data card -->
    <div class="data-card">

      <!-- Loading -->
      <div v-if="loading" class="state-block">
        <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
        <p class="mt-3 text-muted-cell">Loading bank accounts...</p>
      </div>

      <!-- Empty -->
      <div v-else-if="filtered.length === 0" class="state-block">
        <div class="state-icon state-icon--blue"><font-awesome-icon icon="university" /></div>
        <p class="state-title">{{ accounts.length === 0 ? 'No bank accounts found' : 'No results match your search' }}</p>
        <p class="state-sub">{{ accounts.length === 0 ? 'Add your first business bank account to get started.' : 'Try a different search term.' }}</p>
        <button v-if="accounts.length === 0" class="btn-primary-action mt-3" @click="openCreate" :disabled="permission.create_bank != 1">
          <font-awesome-icon icon="plus" /> Add Bank Account
        </button>
      </div>

      <template v-else>
        <!-- Toolbar -->
        <div class="table-toolbar">
          <span class="table-count">Showing <strong>{{ filtered.length }}</strong> of <strong>{{ accounts.length }}</strong> account(s)</span>
        </div>

        <!-- Table -->
        <div class="table-wrap">
          <table class="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Bank Name</th>
                <th>Account Name</th>
                <th>Account No.</th>
                <th>Type</th>
                <th>Currency</th>
                <th>Default</th>
                <th class="col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(acct, index) in filtered" :key="acct.id">

                <td class="text-muted-cell">{{ index + 1 }}</td>

                <td>
                  <div class="bank-cell">
                    <div class="bank-icon"><font-awesome-icon icon="university" /></div>
                    <span class="bank-name">{{ acct.bank_name }}</span>
                  </div>
                </td>

                <td class="text-muted-cell">{{ acct.account_name }}</td>

                <td>
                  <span class="acct-badge">{{ acct.account_number }}</span>
                </td>

                <td>
                  <span class="type-badge" :class="'type-' + acct.account_type">{{ acct.account_type }}</span>
                </td>

                <td>
                  <span class="code-badge">{{ acct.currency_code }}</span>
                </td>

                <td>
                  <label class="toggle-switch">
                    <input
                      type="checkbox"
                      :true-value="true"
                      :false-value="false"
                      :checked="acct.is_default"
                      :disabled="permission.update_bank != 1 || acct.is_default"
                      @change="setDefault(acct)"
                    />
                    <span class="toggle-track"><span class="toggle-thumb"></span></span>
                    <span class="toggle-label" :class="acct.is_default ? 'on' : 'off'">
                      {{ acct.is_default ? 'Default' : 'No' }}
                    </span>
                  </label>
                </td>

                <td class="col-actions">
                  <div class="actions-cell">
                    <button
                      class="action-btn action-btn--edit"
                      title="Edit"
                      :disabled="permission.update_bank != 1"
                      @click="openEdit(acct)"
                    >
                      <font-awesome-icon icon="pen" />
                    </button>
                    <button
                      class="action-btn action-btn--delete"
                      title="Delete"
                      :disabled="permission.delete_bank != 1"
                      @click="openDelete(acct)"
                    >
                      <font-awesome-icon icon="trash" />
                    </button>
                  </div>
                </td>

              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </div>

    <!-- ── Create Modal ── -->
    <div class="modal-overlay" :class="{ open: createOpen }" @click.self="closeCreate">
      <div class="modal-box">
        <div class="modal-header-styled">
          <span class="modal-title-styled">Add Bank Account</span>
          <button class="modal-close-btn" @click="closeCreate"><font-awesome-icon icon="times" /></button>
        </div>
        <form @submit.prevent="createAccount">
          <div class="modal-body-styled">
            <div class="fields-row">
              <div class="form-group-styled">
                <label class="form-label-styled">Bank Name <span class="req">*</span></label>
                <input v-model="form.bank_name" type="text" class="form-input-styled" placeholder="e.g. First Bank" required />
              </div>
              <div class="form-group-styled">
                <label class="form-label-styled">Account Name <span class="req">*</span></label>
                <input v-model="form.account_name" type="text" class="form-input-styled" placeholder="Name on account" required />
              </div>
            </div>
            <div class="fields-row">
              <div class="form-group-styled">
                <label class="form-label-styled">Account Number <span class="req">*</span></label>
                <input v-model="form.account_number" type="text" class="form-input-styled" placeholder="10-digit account number" inputmode="numeric" required />
              </div>
              <div class="form-group-styled">
                <label class="form-label-styled">Account Type</label>
                <select v-model="form.account_type" class="form-input-styled">
                  <option value="current">Current</option>
                  <option value="savings">Savings</option>
                  <option value="domiciliary">Domiciliary</option>
                </select>
              </div>
            </div>
            <div class="fields-row">
              <div class="form-group-styled">
                <label class="form-label-styled">Currency</label>
                <select v-model="form.currency_code" class="form-input-styled">
                  <option value="" disabled>Choose currency</option>
                  <option v-for="c in currencies" :key="c.id" :value="c.code">{{ c.name }} ({{ c.code }})</option>
                </select>
              </div>
              <div class="form-group-styled">
                <label class="form-label-styled">Opening Balance</label>
                <input v-model="form.opening_balance" type="number" step="0.01" class="form-input-styled" placeholder="0.00" />
              </div>
            </div>
          </div>
          <div class="modal-footer-styled">
            <button type="button" class="btn-ghost" @click="closeCreate">Cancel</button>
            <button type="submit" class="btn-primary-action" :disabled="!form.bank_name.trim() || !form.account_name.trim() || !form.account_number.trim() || crudLoading">
              <font-awesome-icon v-if="crudLoading" icon="spinner" spin class="me-1" />
              {{ crudLoading ? 'Saving...' : 'Add Account' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- ── Edit Modal ── -->
    <div class="modal-overlay" :class="{ open: editOpen }" @click.self="closeEdit">
      <div class="modal-box">
        <div class="modal-header-styled">
          <span class="modal-title-styled">Edit Bank Account</span>
          <button class="modal-close-btn" @click="closeEdit"><font-awesome-icon icon="times" /></button>
        </div>
        <form @submit.prevent="updateAccount">
          <div class="modal-body-styled">
            <div class="fields-row">
              <div class="form-group-styled">
                <label class="form-label-styled">Bank Name <span class="req">*</span></label>
                <input v-model="editForm.bank_name" type="text" class="form-input-styled" placeholder="e.g. First Bank" required />
              </div>
              <div class="form-group-styled">
                <label class="form-label-styled">Account Name <span class="req">*</span></label>
                <input v-model="editForm.account_name" type="text" class="form-input-styled" placeholder="Name on account" required />
              </div>
            </div>
            <div class="fields-row">
              <div class="form-group-styled">
                <label class="form-label-styled">Account Number <span class="req">*</span></label>
                <input v-model="editForm.account_number" type="text" class="form-input-styled" placeholder="10-digit account number" inputmode="numeric" required />
              </div>
              <div class="form-group-styled">
                <label class="form-label-styled">Account Type</label>
                <select v-model="editForm.account_type" class="form-input-styled">
                  <option value="current">Current</option>
                  <option value="savings">Savings</option>
                  <option value="domiciliary">Domiciliary</option>
                </select>
              </div>
            </div>
            <div class="fields-row">
              <div class="form-group-styled">
                <label class="form-label-styled">Currency</label>
                <select v-model="editForm.currency_code" class="form-input-styled">
                  <option value="" disabled>Choose currency</option>
                  <option v-for="c in currencies" :key="c.id" :value="c.code">{{ c.name }} ({{ c.code }})</option>
                </select>
              </div>
              <div class="form-group-styled" style="justify-content:flex-end;">
                <label class="form-label-styled">&nbsp;</label>
                <label class="default-check">
                  <input type="checkbox" v-model="editForm.is_default" />
                  <span>Set as default account</span>
                </label>
              </div>
            </div>
          </div>
          <div class="modal-footer-styled">
            <button type="button" class="btn-ghost" @click="closeEdit">Cancel</button>
            <button type="submit" class="btn-primary-action" :disabled="!editForm.bank_name.trim() || !editForm.account_name.trim() || !editForm.account_number.trim() || crudLoading">
              <font-awesome-icon v-if="crudLoading" icon="spinner" spin class="me-1" />
              {{ crudLoading ? 'Saving...' : 'Save Changes' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- ── Delete Modal ── -->
    <div class="modal-overlay" :class="{ open: deleteOpen }" @click.self="closeDelete">
      <div class="modal-box">
        <div class="modal-body-styled" style="padding:32px 24px 24px; text-align:center;">
          <div class="delete-icon-wrap"><font-awesome-icon icon="trash" style="font-size:26px;" /></div>
          <div class="delete-title">Delete Bank Account</div>
          <p class="delete-sub">
            Are you sure you want to delete <strong>{{ deleteTarget.account_name }}</strong> ({{ deleteTarget.bank_name }})? This action cannot be undone.
          </p>
        </div>
        <div class="modal-footer-styled" style="justify-content:center; gap:12px;">
          <button type="button" class="btn-ghost" @click="closeDelete">Cancel</button>
          <button type="button" class="btn-danger-action" :disabled="crudLoading" @click="deleteAccount">
            <font-awesome-icon v-if="crudLoading" icon="spinner" spin class="me-1" />
            Yes, Delete
          </button>
        </div>
      </div>
    </div>

    <!-- Backdrop -->
    <div v-if="createOpen || editOpen || deleteOpen" class="modal-backdrop-custom" />

  </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';
import AdminHeader from "./administrationheadertab.vue";
import { useStore } from 'vuex';

const BLANK_FORM = () => ({
  bank_name: '', account_name: '', account_number: '',
  account_type: 'current', currency_code: 'NGN', opening_balance: '',
});

export default {
  name: 'BankAccounts',
  components: { AdminHeader },

  data() {
    return {
      accounts: [],
      currencies: [],
      loading:     false,
      crudLoading: false,
      search:      '',
      searchTimer: null,

      form:     BLANK_FORM(),
      editForm: { id: '', bank_name: '', account_name: '', account_number: '', account_type: 'current', currency_code: 'NGN', is_default: false },
      deleteTarget: { id: '', account_name: '', bank_name: '' },

      createOpen: false,
      editOpen:   false,
      deleteOpen: false,

      toast: useToast(),
      store: useStore(),
    };
  },

  watch: {
    search() { this.debouncedGetAccounts(); },
  },

  computed: {
    permission() {
      return this.store.state.getCurrentUserPermission;
    },
    filtered() {
      return this.accounts;
    },
  },

  methods: {
    // ── Modal helpers ────────────────────────────────────────────────
    openCreate() { this.form = BLANK_FORM(); this.createOpen = true; },
    closeCreate() { this.createOpen = false; },

    openEdit(acct) {
      this.editForm = {
        id:             acct.id,
        bank_name:      acct.bank_name,
        account_name:   acct.account_name,
        account_number: acct.account_number,
        account_type:   acct.account_type ?? 'current',
        currency_code:  acct.currency_code ?? 'NGN',
        is_default:     !!acct.is_default,
      };
      this.editOpen = true;
    },
    closeEdit() { this.editOpen = false; },

    openDelete(acct) {
      this.deleteTarget = { id: acct.id, account_name: acct.account_name, bank_name: acct.bank_name };
      this.deleteOpen = true;
    },
    closeDelete() { this.deleteOpen = false; },

    // ── Data ─────────────────────────────────────────────────────────
    async getCurrencies() {
      try {
        const { data } = await axios.get('settings/currency');
        this.currencies = data.data.data ?? [];
      } catch {
        // non-critical — dropdown falls back to empty
      }
    },

    debouncedGetAccounts() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.getAccounts(), 300);
    },

    async getAccounts() {
      this.loading = true;
      try {
        const params = {};
        if (this.search.trim()) params.search = this.search.trim();
        const { data } = await axios.get('settings/bank-accounts', { params });
        this.accounts = data.data ?? [];
      } catch (err) {
        this.toast.error(err.response?.data?.message ?? 'Could not load bank accounts.');
      } finally {
        this.loading = false;
      }
    },

    async createAccount() {
      this.crudLoading = true;
      try {
        await axios.post('settings/bank-accounts', {
          bank_name:       this.form.bank_name,
          account_name:    this.form.account_name,
          account_number:  this.form.account_number,
          account_type:    this.form.account_type,
          currency_code:   this.form.currency_code || 'NGN',
          opening_balance: this.form.opening_balance || 0,
        });
        this.toast.success('Bank account added successfully.');
        this.closeCreate();
        this.getAccounts();
      } catch (err) {
        if (err.response?.status === 422) {
          const msg = err.response.data?.message;
          this.toast.warning(Array.isArray(msg) ? msg[0] : (msg ?? 'Invalid input.'));
        } else {
          this.toast.error('An error occurred. Please try again.');
        }
      } finally {
        this.crudLoading = false;
      }
    },

    async updateAccount() {
      this.crudLoading = true;
      try {
        await axios.put(`settings/bank-accounts/${this.editForm.id}`, {
          bank_name:      this.editForm.bank_name,
          account_name:   this.editForm.account_name,
          account_number: this.editForm.account_number,
          account_type:   this.editForm.account_type,
          currency_code:  this.editForm.currency_code || 'NGN',
          is_default:     this.editForm.is_default,
        });
        this.toast.success('Bank account updated successfully.');
        this.closeEdit();
        this.getAccounts();
      } catch (err) {
        if (err.response?.status === 422) {
          this.toast.warning('Invalid input. Please check and try again.');
        } else {
          this.toast.error('An error occurred. Please try again.');
        }
      } finally {
        this.crudLoading = false;
      }
    },

    async setDefault(acct) {
      try {
        await axios.put(`settings/bank-accounts/${acct.id}`, {
          bank_name:      acct.bank_name,
          account_name:   acct.account_name,
          account_number: acct.account_number,
          account_type:   acct.account_type,
          currency_code:  acct.currency_code,
          is_default:     true,
        });
        this.toast.success(`${acct.account_name} set as default account.`);
        this.getAccounts();
      } catch {
        this.toast.error('Could not update default account.');
        this.getAccounts();
      }
    },

    async deleteAccount() {
      this.crudLoading = true;
      try {
        await axios.delete(`settings/bank-accounts/${this.deleteTarget.id}`);
        this.toast.success('Bank account deleted successfully.');
        this.closeDelete();
        this.getAccounts();
      } catch (err) {
        this.toast.error(err.response?.data?.message ?? 'Bank account could not be deleted.');
      } finally {
        this.crudLoading = false;
      }
    },
  },

  mounted() { this.getAccounts(); this.getCurrencies(); },
};
</script>

<style scoped>
/* ── Page ────────────────────────────────────────────────────────────── */
.page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:14px; margin-bottom:20px; }
.page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub     { font-size:13px; color:#7a90b0; margin:0; }
.page-actions { display:flex; align-items:center; flex-wrap:wrap; gap:8px; }

/* ── Search ──────────────────────────────────────────────────────────── */
.search-wrap  { position:relative; display:flex; align-items:center; }
.search-icon  { position:absolute; left:11px; color:#7a90b0; font-size:13px; pointer-events:none; }
.search-input { padding:8px 12px 8px 33px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; width:220px; outline:none; transition:border-color .15s; }
.search-input:focus { border-color:#004AAD; }

/* ── Buttons ─────────────────────────────────────────────────────────── */
.btn-primary-action { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:8px 16px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; white-space:nowrap; }
.btn-primary-action:hover:not(:disabled) { background:#003b8e; }
.btn-primary-action:disabled { opacity:.55; cursor:not-allowed; }
.btn-ghost { display:inline-flex; align-items:center; gap:7px; background:#f4f7fb; color:#3d5068; border:1px solid #e4eaf2; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; cursor:pointer; transition:background .15s; }
.btn-ghost:hover { background:#e4eaf2; }
.btn-danger-action { display:inline-flex; align-items:center; gap:7px; background:#e03535; color:#fff; font-size:13px; font-weight:600; padding:9px 22px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; }
.btn-danger-action:hover:not(:disabled) { background:#c02828; }
.btn-danger-action:disabled { opacity:.55; cursor:not-allowed; }
.mt-3 { margin-top:12px; }
.me-1 { margin-right:4px; }

/* ── Data card ───────────────────────────────────────────────────────── */
.data-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }

/* ── States ──────────────────────────────────────────────────────────── */
.state-block      { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; }
.state-icon       { width:60px; height:60px; border-radius:16px; display:flex; align-items:center; justify-content:center; font-size:24px; margin-bottom:16px; }
.state-icon--blue { background:rgba(0,74,173,.08); color:#004AAD; }
.state-title      { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
.state-sub        { font-size:13px; color:#7a90b0; margin:0; }
.text-muted-cell  { color:#4a5e78; }

/* ── Table toolbar ───────────────────────────────────────────────────── */
.table-toolbar { display:flex; align-items:center; justify-content:space-between; padding:14px 20px; border-bottom:1px solid #e4eaf2; }
.table-count   { font-size:13px; color:#7a90b0; }
.table-count strong { color:#1a2b45; }

/* ── Table ───────────────────────────────────────────────────────────── */
.table-wrap  { overflow-x:auto; }
.data-table  { width:100%; border-collapse:collapse; }
.data-table thead tr { background:#f8fafc; }
.data-table thead th { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:12px 16px; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
.data-table tbody tr { border-bottom:1px solid #f0f4f8; transition:background .12s; }
.data-table tbody tr:last-child { border-bottom:none; }
.data-table tbody tr:hover { background:#f8fafc; }
.data-table tbody td { padding:12px 16px; font-size:13px; vertical-align:middle; }
.col-actions { width:90px; text-align:center; }

/* ── Bank cell ───────────────────────────────────────────────────────── */
.bank-cell { display:flex; align-items:center; gap:10px; }
.bank-icon { width:30px; height:30px; border-radius:7px; background:rgba(0,74,173,.08); color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:12px; flex-shrink:0; }
.bank-name { font-size:13px; font-weight:500; color:#1a2b45; }

/* ── Badges ──────────────────────────────────────────────────────────── */
.code-badge  { display:inline-block; font-family:monospace; font-size:12px; font-weight:700; background:#f4f7fb; color:#1a2b45; border:1px solid #e4eaf2; border-radius:6px; padding:2px 8px; }
.acct-badge  { display:inline-block; font-family:monospace; font-size:12px; font-weight:600; color:#3d5068; background:#f8fafc; border:1px solid #e4eaf2; border-radius:6px; padding:2px 8px; }
.type-badge  { display:inline-block; font-size:11.5px; font-weight:600; border-radius:5px; padding:2px 8px; text-transform:capitalize; }
.type-current     { background:#e8f4fd; color:#004AAD; }
.type-savings     { background:#e8f9ee; color:#15803d; }
.type-domiciliary { background:#fef3e8; color:#b45309; }

/* ── Toggle switch ───────────────────────────────────────────────────── */
.toggle-switch { display:inline-flex; align-items:center; gap:8px; cursor:pointer; user-select:none; }
.toggle-switch input { display:none; }
.toggle-track  { position:relative; width:36px; height:20px; border-radius:20px; background:#d1d9e8; transition:background .2s; flex-shrink:0; }
.toggle-switch input:checked + .toggle-track { background:#16a34a; }
.toggle-thumb  { position:absolute; top:3px; left:3px; width:14px; height:14px; border-radius:50%; background:#fff; box-shadow:0 1px 3px rgba(0,0,0,.2); transition:transform .2s; }
.toggle-switch input:checked + .toggle-track .toggle-thumb { transform:translateX(16px); }
.toggle-label  { font-size:12px; font-weight:600; }
.toggle-label.on  { color:#16a34a; }
.toggle-label.off { color:#7a90b0; }

/* ── Action buttons ──────────────────────────────────────────────────── */
.actions-cell      { display:flex; align-items:center; justify-content:center; gap:6px; }
.action-btn        { width:30px; height:30px; border-radius:7px; border:1px solid #dde5f0; background:#fff; display:flex; align-items:center; justify-content:center; font-size:12px; color:#7a90b0; cursor:pointer; transition:all .15s; }
.action-btn--edit:hover   { background:#e8f4fd; color:#004AAD; border-color:#c5d6f5; }
.action-btn--delete:hover { background:#fff0f0; color:#e03535; border-color:#f5c6c6; }
.action-btn:disabled { opacity:.4; cursor:not-allowed; }

/* ── Modal ───────────────────────────────────────────────────────────── */
.modal-overlay         { position:fixed; inset:0; z-index:1050; display:flex; align-items:center; justify-content:center; opacity:0; pointer-events:none; transition:opacity .2s; }
.modal-overlay.open    { opacity:1; pointer-events:all; }
.modal-backdrop-custom { position:fixed; inset:0; z-index:1040; background:rgba(15,25,50,.45); backdrop-filter:blur(2px); }
.modal-box             { background:#fff; border-radius:14px; width:100%; max-width:520px; margin:16px; box-shadow:0 20px 60px rgba(0,0,0,.18); overflow:hidden; transform:translateY(16px); transition:transform .22s ease; z-index:1051; position:relative; }
.modal-overlay.open .modal-box { transform:translateY(0); }
.modal-header-styled   { padding:20px 24px 16px; border-bottom:1px solid #e4eaf2; display:flex; align-items:center; justify-content:space-between; }
.modal-title-styled    { font-size:16px; font-weight:700; color:#1a2b45; }
.modal-close-btn       { width:30px; height:30px; border-radius:7px; border:1px solid #dde5f0; background:transparent; cursor:pointer; display:flex; align-items:center; justify-content:center; color:#7a90b0; font-size:13px; transition:background .15s; }
.modal-close-btn:hover { background:#f4f7fb; }
.modal-body-styled     { padding:20px 24px; display:flex; flex-direction:column; gap:14px; }
.modal-footer-styled   { padding:16px 24px; border-top:1px solid #e4eaf2; display:flex; justify-content:flex-end; gap:10px; }

/* ── Modal form ──────────────────────────────────────────────────────── */
.fields-row { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
.form-group-styled  { display:flex; flex-direction:column; gap:6px; }
.form-label-styled  { font-size:13px; font-weight:600; color:#1a2b45; }
.req                { color:#e03535; }
.form-input-styled  { width:100%; padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; transition:border-color .15s, box-shadow .15s; font-family:inherit; box-sizing:border-box; }
.form-input-styled:focus { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.08); }
.default-check { display:inline-flex; align-items:center; gap:8px; font-size:13px; color:#1a2b45; cursor:pointer; }
.default-check input { width:15px; height:15px; cursor:pointer; accent-color:#004AAD; }

/* ── Delete modal ────────────────────────────────────────────────────── */
.delete-icon-wrap { width:64px; height:64px; border-radius:50%; background:#fff0f0; color:#e03535; display:flex; align-items:center; justify-content:center; margin:0 auto 16px; }
.delete-title     { font-size:17px; font-weight:700; color:#1a2b45; margin-bottom:8px; }
.delete-sub       { font-size:13px; color:#7a90b0; line-height:1.5; }

/* ── Responsive ──────────────────────────────────────────────────────── */
@media (max-width:768px) {
  .page-header, .page-actions { flex-direction:column; align-items:stretch; }
  .search-input { width:100%; }
  .fields-row   { grid-template-columns:1fr; }
}
</style>
